import { ReactElement, cloneElement, useEffect, useRef, useState } from 'react';

interface Props {
  direction?: 'left' | 'right' | 'top' | 'bottom';
  threshold?: number;
  translateValue?: string;
  children: ReactElement;
}

const SlideInOnScroll = ({
  direction = 'left',
  threshold = 0.2,
  translateValue = '15rem',
  children,
}: Props) => {
  const ref = useRef<HTMLElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isDesktop, setIsDesktop] = useState(true);

  useEffect(() => {
    const check = () => {
      setIsDesktop(window.innerWidth > 1080);
    };
    check();
    window.addEventListener('resize', check);
    return () => {
      window.removeEventListener('resize', check);
    };
  }, []);

  useEffect(() => {
    if (!isDesktop) return;
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold }
    );

    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [threshold, isDesktop]);

  if (!isDesktop) return children;

  // Determine initial transform based on direction
  let initialTransform = '';
  switch (direction) {
    case 'left':
      initialTransform = `translateX(-${translateValue})`;
      break;
    case 'right':
      initialTransform = `translateX(${translateValue})`;
      break;
    case 'top':
      initialTransform = `translateY(-${translateValue})`;
      break;
    case 'bottom':
      initialTransform = `translateY(${translateValue})`;
      break;
  }

  const style = {
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translate(0, 0)' : initialTransform,
    transition:
      'transform 0.8s cubic-bezier(0.57, 0.61, 0, 0.97), opacity 0.8s cubic-bezier(0.57, 0.61, 0, 0.97)',
  };

  return cloneElement(children, {
    ref,
    style: { ...(children.props.style || {}), ...style },
  });
};

export default SlideInOnScroll;
